import { BrowserRouter, Routes, Route, useParams, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import BotOverview from "./pages/BotOverview";
import LiveConversations from "./pages/LiveConversations";
import Sidebar from "./pages/TopSidebar";
import CreateBot from "./pages/CreateBot";
import KnowledgeBase from "./pages/KnowledgeBase";
import GeneratePrompts from "./pages/GeneratePrompts";
import BotConfig from "./pages/BotConfig";
import RecomSystem from "./pages/RecomSystem"; // <-- ADDED IMPORT
import KnowledgeAfterOnboard from "./pages/KnowledgeAfterOnboard";
import PromptAfterOnboarding from "./pages/PromptAfterOnboarding";
import ChatUIWrapper from "./pages/ChatUIWrapper";
import PublicChatPage from "./pages/PublicChatPage";
import ChatUIPublicUsers from "./pages/ChatUIPublicUsers";
import Billing from "./pages/Billing";
import Checkout from "./pages/Checkout";
import MonitorConversations from "./pages/MonitorConversations";
import Analytics from "./pages/Analytics";
import VerifyOTP from "./pages/VerifyOTP";
import BrandingSettings from "./pages/BrandingSettings";
import BillingHistory from "./pages/BillingHistory";
import TenantSettings from "./pages/TenantSettings";
import MonitorConversationsAdmin from "./pages/MonitorConversationsAdmin";
import EmbedChatUI from "./pages/EmbedChatUI";
import KBGaps from "./pages/KBGaps";
import UserRecords from "./pages/UserRecords";
import EscalationRecords from "./pages/EscalationRecords";
import { GoogleOAuthProvider } from "@react-oauth/google";
import AdminDashboard from "./pages/AdminDashboard";
import AdminLogin from "./pages/AdminLogin";
import ResetPassword from "./pages/ResetPassword";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import { decodeJwt, isJwtExpired } from "./utils/jwt";

const ProtectedRoute = ({ children }) => {
  const { tenantId } = useParams();
  const [isChecking, setIsChecking] = useState(true);
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const storedTenantId = localStorage.getItem("tenant_id");

    if (!token) {
      setIsValid(false);
      setIsChecking(false);
      return;
    }

    setIsValid(true);
    setIsChecking(false);
  }, [tenantId]);

  if (isChecking) {
    return <div>Loading...</div>;
  }

  if (!isValid) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const AdminRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  const claims = decodeJwt(token);

  if (!claims?.is_admin || isJwtExpired(token)) {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

const NoSidebarLayout = ({ children }) => (
  <div className="min-h-screen">{children}</div>
);

const BotLayout = ({ children }) => (
  <div className="flex min-h-screen">
    <Sidebar />
    <main className="flex-1 bg-[#F8F4F2]">{children}</main>
  </div>
);

function LiveConversationsPage() {
  const { tenantId, botId } = useParams();
  return (
    <div className="p-6 h-screen">
      <LiveConversations tenantId={tenantId} botId={botId} />
    </div>
  );
}

function RouteWatcher() {
  const location = useLocation();

  useEffect(() => {
    const toggleUserback = () => {
      if (!window.Userback) return;
      
      const isReady = window.Userback._isReady 
        || (window.Userback.show && window.Userback.hide && typeof window.Userback.show === 'function');
      
      if (!isReady) {
        setTimeout(toggleUserback, 150);
        return;
      }

      const path = location.pathname;
      const isLoginPage = path === "/" || path === "/login" || path === "/admin";

      try {
        if (isLoginPage) {
          window.Userback.hide?.();
        } else {
          window.Userback.show?.();
        }
      } catch (err) {
        console.warn('Userback toggle failed:', err);
      }
    };

    toggleUserback();
  }, [location]);

  return null;
}

function App() {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) return;
    
    const isLocalhost =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";

    if (isLocalhost) return; 
    if (document.getElementById("userback-script")) return;

    window.Userback = {
      access_token: "A-3OeYWTsLcfowDiEB53eonYNhJ",
      onReady: function() {
        window.Userback._isReady = true;
      }
    };

    const script = document.createElement("script");
    script.id = "userback-script";
    script.src = "https://static.userback.io/widget/v1.js";
    script.async = true;
    script.crossOrigin = "anonymous";

    script.onerror = () => {
      console.error("Failed to load Userback widget");
      window.Userback._loadError = true;
    };

    script.onload = () => {
      console.log("Userback widget loaded successfully");
    };

    document.body.appendChild(script);

    return () => {
      const existing = document.getElementById("userback-script");
      if (existing) existing.remove();
    };
  }, [isMounted]);

  return (
    <GoogleOAuthProvider clientId="200320018081-8v6qo1fs6nspf2976f979trfqtb68i9c.apps.googleusercontent.com">
      <BrowserRouter>
        <RouteWatcher />
        <Routes>
          <Route path="/chat/:tenantId/:botId" element={<ChatUIPublicUsers />} />
          <Route path="/embed-chat/:tenantId/:botId" element={<EmbedChatUI />} />
          <Route path="/" element={<Login />} />
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin-dashboard" element={
            <AdminRoute>
              <NoSidebarLayout>
                <AdminDashboard />
              </NoSidebarLayout>
            </AdminRoute>
          } />
          <Route path="/verify-otp" element={
            <NoSidebarLayout>
              <VerifyOTP />
            </NoSidebarLayout>
          } />
          <Route path="/dashboard/:tenantId" element={
            <ProtectedRoute>
              <NoSidebarLayout>
                <Dashboard />
              </NoSidebarLayout>
            </ProtectedRoute>
          } />
          <Route path="/analytics/:tenantId" element={
            <ProtectedRoute>
              <NoSidebarLayout>
                <Analytics />
              </NoSidebarLayout>
            </ProtectedRoute>
          } />
          <Route path="/billing/:tenantId" element={
            <ProtectedRoute>
              <NoSidebarLayout>
                <Billing />
              </NoSidebarLayout>
            </ProtectedRoute>
          } />
          <Route path="/billing-history/:tenantId" element={
            <ProtectedRoute>
              <NoSidebarLayout>
                <BillingHistory />
              </NoSidebarLayout>
            </ProtectedRoute>
          } />
          <Route path="/checkout/:tenantId" element={
            <ProtectedRoute>
              <NoSidebarLayout>
                <Checkout />
              </NoSidebarLayout>
            </ProtectedRoute>
          } />
          <Route path="/create-bot/:tenantId" element={
            <ProtectedRoute>
              <NoSidebarLayout>
                <CreateBot />
              </NoSidebarLayout>
            </ProtectedRoute>
          } />
          
          {/* Agent Configuration Route */}
          <Route path="/configuration/:tenantId/:botId/config" element={
            <ProtectedRoute>
              <BotLayout>
                <BotConfig />
              </BotLayout>
            </ProtectedRoute>
          } />

          {/* Recommendation System Route */}
          <Route path="/configuration/:tenantId/:botId/recomsystem" element={
            <ProtectedRoute>
              <BotLayout>
                <RecomSystem />
              </BotLayout>
            </ProtectedRoute>
          } />

          <Route path="/knowledge/:tenantId/:botId" element={
            <ProtectedRoute>
              <NoSidebarLayout>
                <KnowledgeBase />
              </NoSidebarLayout>
            </ProtectedRoute>
          } />
          <Route path="/knowledgeafteronboard/:tenantId/:botId" element={
            <ProtectedRoute>
              <BotLayout>
                <KnowledgeAfterOnboard />
              </BotLayout>
            </ProtectedRoute>
          } />
          <Route path="/configuration/:tenantId/:botId/prompts" element={
            <ProtectedRoute>
              <BotLayout>
                <PromptAfterOnboarding />
              </BotLayout>
            </ProtectedRoute>
          } />
          <Route path="/generate-prompts/:tenantId/:botId" element={
            <ProtectedRoute>
              <NoSidebarLayout>
                <GeneratePrompts />
              </NoSidebarLayout>
            </ProtectedRoute>
          } />
          <Route path="/overview/:tenantId/:botId" element={
            <ProtectedRoute>
              <BotLayout>
                <BotOverview />
              </BotLayout>
            </ProtectedRoute>
          } />
          <Route path="/overview/:tenantId/:botId/live" element={
            <ProtectedRoute>
              <BotLayout>
                <LiveConversationsPage />
              </BotLayout>
            </ProtectedRoute>
          } />
          <Route path="/test-assist/:tenantId/:botId/chat/:sessionId?" element={
            <ProtectedRoute>
              <BotLayout>
                <ChatUIWrapper />
              </BotLayout>
            </ProtectedRoute>
          } />
          <Route path="/monitor/conversations/:tenantId/:botId" element={
            <ProtectedRoute>
              <BotLayout>
                <MonitorConversations />
              </BotLayout>
            </ProtectedRoute>
          } />
          <Route path="/monitor/conversationsad/:tenantId/:botId" element={
            <ProtectedRoute>
              <BotLayout>
                <MonitorConversationsAdmin />
              </BotLayout>
            </ProtectedRoute>
          } />
          <Route path="/leads/:tenantId/:botId" element={
            <ProtectedRoute>
              <BotLayout>
                <EscalationRecords />
              </BotLayout>
            </ProtectedRoute>
          } />
          <Route path="/lead-users/:tenantId/:botId" element={
            <ProtectedRoute>
              <BotLayout>
                <UserRecords />
              </BotLayout>
            </ProtectedRoute>
          } />
          <Route path="/kb-gaps/:tenantId/:botId" element={
            <ProtectedRoute>
              <BotLayout>
                <KBGaps />
              </BotLayout>
            </ProtectedRoute>
          } />
          <Route path="/settings/:tenantId/:botId" element={
            <ProtectedRoute>
              <BotLayout>
                <BrandingSettings />
              </BotLayout>
            </ProtectedRoute>
          } />
          <Route path="/tenant-settings/:tenantId" element={
            <ProtectedRoute>
              <TenantSettings />
            </ProtectedRoute>
          } />
        </Routes>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

export default App;